import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  FlatList,
  Modal,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  Alert,
  Dimensions,
  Image,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width, height } = Dimensions.get('window');

const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
  applications: `${API_BASE_URL}/api/data/applications`,
  interviews: `${API_BASE_URL}/api/data/interviews`,
  jobs: `${API_BASE_URL}/api/data/jobs`,
  offers: `${API_BASE_URL}/api/data/job_offers`,
  moveStage: (id) => `${API_BASE_URL}/api/recruitment/applications/${id}/stage`,
  createOffer: `${API_BASE_URL}/api/recruitment/offers/create`,
  scoreApp: (id) => `${API_BASE_URL}/api/recruitment/applications/${id}/score`,
  scoreAll: `${API_BASE_URL}/api/recruitment/jobs`,
  scoreOne: `${API_BASE_URL}/api/recruitment/applications`,
};
const api = axios.create({
  baseURL: API_BASE_URL, timeout: 15000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
});
api.interceptors.request.use(async (config) => {
  try {
    const token = await AsyncStorage.getItem('auth_token');
    if (token) config.headers.Authorization = 'Bearer ' + token;
    const companyId = await AsyncStorage.getItem('company_id');
    if (companyId) config.params = { ...config.params, company_id: parseInt(companyId) };
  } catch {} return config;
}, (error) => Promise.reject(error));
const safeData = r => (r?.data?.success && Array.isArray(r.data.data)) ? r.data.data : [];

export default function QualifiedApplicantsPipelineScreen({ navigation, route }) {
  const jobId = route?.params?.jobId || route?.params?.job_id || null;
  const jobTitle = route?.params?.jobTitle || '';
  const [companyLogo, setCompanyLogo] = useState(route?.params?.companyLogo || null);
  const [companyName, setCompanyName] = useState(route?.params?.companyName || '');
  const [companyId, setCompanyId] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTab, setSelectedTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('overall_score');
  const [showFinalInterviewModal, setShowFinalInterviewModal] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // Final interview types
  const finalInterviewTypes = [
    {
      id: 'executive',
      name: 'Executive Interview',
      description: 'Final interview with C-level executives',
      duration: 60,
      interviewers: ['CEO', 'CTO', 'VP Engineering'],
      focus: 'Leadership & Vision',
    },
    {
      id: 'panel',
      name: 'Panel Interview',
      description: 'Multi-interviewer panel discussion',
      duration: 90,
      interviewers: ['Team Lead', 'Senior Engineer', 'Product Manager'],
      focus: 'Team Fit & Collaboration',
    },
    {
      id: 'presentation',
      name: 'Presentation Interview',
      description: 'Candidate presents solution to business case',
      duration: 45,
      interviewers: ['Department Head', 'Senior Manager'],
      focus: 'Communication & Problem Solving',
    },
    {
      id: 'cultural',
      name: 'Cultural Fit Interview',
      description: 'Deep dive into company culture alignment',
      duration: 30,
      interviewers: ['HR Director', 'Team Members'],
      focus: 'Values & Culture Alignment',
    },
  ];

  // Mock candidate data - 27 qualified candidates
  const generateMockCandidates = () => {
    const names = [
      'Sarah Johnson', 'Michael Chen', 'Emma Williams', 'James Rodriguez', 'Olivia Brown',
      'David Kim', 'Sophie Taylor', 'Alexander Davis', 'Isabella Garcia', 'William Wilson',
      'Ava Martinez', 'Benjamin Anderson', 'Mia Thompson', 'Lucas White', 'Charlotte Harris',
      'Henry Clark', 'Amelia Lewis', 'Sebastian Young', 'Harper King', 'Owen Wright',
      'Evelyn Lopez', 'Noah Hill', 'Abigail Scott', 'Liam Green', 'Emily Adams',
    ];

    const positions = [
      'Senior Software Engineer', 'Product Manager', 'UX Designer', 'Data Scientist',
      'DevOps Engineer', 'Frontend Developer', 'Backend Developer', 'Full Stack Developer',
      'Mobile Developer', 'QA Engineer', 'Business Analyst', 'Project Manager',
    ];

    const companies = [
      'Google', 'Microsoft', 'Amazon', 'Apple', 'Meta', 'Netflix', 'Spotify', 'Uber',
      'Airbnb', 'Tesla', 'Stripe', 'Shopify', 'Slack', 'Adobe', 'Salesforce', 'Oracle',
    ];

    const locations = [
      'London, UK', 'Manchester, UK', 'Birmingham, UK', 'Edinburgh, UK', 'Bristol, UK',
      'Leeds, UK', 'Glasgow, UK', 'Liverpool, UK', 'Sheffield, UK', 'Newcastle, UK',
    ];

    const skills = [
      'React', 'Node.js', 'Python', 'Java', 'TypeScript', 'AWS', 'Docker', 'Kubernetes',
      'Machine Learning', 'Data Analysis', 'UI/UX Design', 'Project Management',
      'Agile', 'Scrum', 'Leadership', 'Communication', 'Problem Solving', 'Teamwork',
    ];

    const qualificationStatuses = ['qualified', 'top_talent', 'exceptional', 'pending_final', 'ready_for_offer'];
    const talentLevels = ['top_talent', 'exceptional', 'outstanding', 'elite'];
    const finalInterviewStatuses = ['not_scheduled', 'scheduled', 'completed', 'pending'];

    const mockCandidates = [];
    
    for (let i = 1; i <= 27; i++) {
      const name = names[Math.floor(Math.random() * names.length)];
      const position = positions[Math.floor(Math.random() * positions.length)];
      const company = companies[Math.floor(Math.random() * companies.length)];
      const location = locations[Math.floor(Math.random() * locations.length)];
      const qualificationStatus = qualificationStatuses[Math.floor(Math.random() * qualificationStatuses.length)];
      const talentLevel = talentLevels[Math.floor(Math.random() * talentLevels.length)];
      const finalInterviewStatus = finalInterviewStatuses[Math.floor(Math.random() * finalInterviewStatuses.length)];
      
      const candidateSkills = [];
      const numSkills = Math.floor(Math.random() * 6) + 4; // 4-9 skills
      for (let j = 0; j < numSkills; j++) {
        const skill = skills[Math.floor(Math.random() * skills.length)];
        if (!candidateSkills.includes(skill)) {
          candidateSkills.push(skill);
        }
      }

      // Generate comprehensive scores (all high since they're qualified)
      const overallScore = Math.floor(Math.random() * 20) + 80; // 80-100
      const technicalScore = Math.floor(Math.random() * 15) + 85; // 85-100
      const interviewScore = Math.floor(Math.random() * 15) + 85; // 85-100
      const testScore = Math.floor(Math.random() * 15) + 85; // 85-100
      const aiMatchScore = Math.floor(Math.random() * 15) + 85; // 85-100
      const culturalFitScore = Math.floor(Math.random() * 10) + 90; // 90-100
      const communicationScore = Math.floor(Math.random() * 10) + 90; // 90-100
      const leadershipScore = Math.floor(Math.random() * 20) + 80; // 80-100

      // Generate final interview details
      const finalInterviewType = finalInterviewTypes[Math.floor(Math.random() * finalInterviewTypes.length)];
      const finalInterviewDate = new Date();
      finalInterviewDate.setDate(finalInterviewDate.getDate() + Math.floor(Math.random() * 7)); // Next week

      mockCandidates.push({
        id: `candidate_${i}`,
        name: `${name} ${i > 15 ? Math.floor(i/3) : ''}`.trim(),
        email: `${name.toLowerCase().replace(' ', '.')}${i > 15 ? i : ''}@email.com`,
        phone: `+44 ${Math.floor(Math.random() * 9000) + 1000} ${Math.floor(Math.random() * 900000) + 100000}`,
        position: position,
        currentCompany: company,
        location: location,
        experience: Math.floor(Math.random() * 12) + 5, // 5-17 years (senior level)
        education: ['Master\'s', 'PhD', 'Bachelor\'s'][Math.floor(Math.random() * 3)],
        skills: candidateSkills,
        salaryExpectation: `£${(Math.floor(Math.random() * 40) + 60) * 1000}`, // £60k-£100k
        
        // Qualification details
        qualificationStatus: qualificationStatus,
        talentLevel: talentLevel,
        qualifiedDate: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
        
        // Comprehensive scores
        overallScore: overallScore,
        technicalScore: technicalScore,
        interviewScore: interviewScore,
        testScore: testScore,
        aiMatchScore: aiMatchScore,
        culturalFitScore: culturalFitScore,
        communicationScore: communicationScore,
        leadershipScore: leadershipScore,
        
        // Performance metrics
        performanceRating: Math.floor(Math.random() * 2) + 4, // 4-5 stars
        consistencyScore: Math.floor(Math.random() * 10) + 90, // 90-100
        growthPotential: Math.floor(Math.random() * 15) + 85, // 85-100
        teamFitScore: Math.floor(Math.random() * 10) + 90, // 90-100
        
        // Final interview details
        finalInterviewStatus: finalInterviewStatus,
        finalInterviewType: finalInterviewType,
        finalInterviewDate: finalInterviewStatus !== 'not_scheduled' ? finalInterviewDate.toISOString() : null,
        finalInterviewScore: finalInterviewStatus === 'completed' ? Math.floor(Math.random() * 15) + 85 : null,
        
        // Strengths and achievements
        keyStrengths: [
          'Exceptional technical skills', 'Strong leadership qualities', 'Excellent communication',
          'Problem-solving expertise', 'Team collaboration', 'Innovation mindset',
          'Strategic thinking', 'Mentoring abilities', 'Cross-functional experience'
        ].slice(0, Math.floor(Math.random() * 4) + 3),
        
        achievements: [
          'Led team of 10+ developers', 'Increased system performance by 40%', 'Delivered 5 major projects',
          'Mentored 15+ junior developers', 'Reduced deployment time by 60%', 'Implemented CI/CD pipeline',
          'Won innovation award', 'Published technical articles', 'Speaker at tech conferences'
        ].slice(0, Math.floor(Math.random() * 3) + 2),
        
        // References and background
        referencesChecked: Math.random() > 0.3,
        backgroundVerified: Math.random() > 0.2,
        portfolioQuality: ['Excellent', 'Outstanding', 'Exceptional'][Math.floor(Math.random() * 3)],
        
        // Offer readiness
        offerReadiness: Math.floor(Math.random() * 20) + 80, // 80-100
        negotiationComplexity: ['Low', 'Medium', 'High'][Math.floor(Math.random() * 3)],
        startDateFlexibility: ['Immediate', '2 weeks', '1 month', '2 months'][Math.floor(Math.random() * 4)],
        
        // Additional metadata
        interviewRounds: Math.floor(Math.random() * 2) + 3, // 3-4 rounds completed
        totalInterviewTime: Math.floor(Math.random() * 120) + 180, // 180-300 minutes
        interviewerFeedback: 'Exceptional candidate with strong technical and leadership skills. Highly recommended for final round.',
        
        // Tags based on performance
        tags: (() => {
          const baseTags = ['Top Talent'];
          if (overallScore >= 95) baseTags.push('Exceptional');
          if (leadershipScore >= 90) baseTags.push('Leadership');
          if (technicalScore >= 95) baseTags.push('Technical Expert');
          if (communicationScore >= 95) baseTags.push('Great Communicator');
          return baseTags;
        })(),
        
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`,
        lastActivity: new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000).toISOString(),
      });
    }

    return mockCandidates.sort((a, b) => b.overallScore - a.overallScore);
  };

  const tabs = [
    { id: 'all', name: 'All', count: 27 },
    { id: 'top_talent', name: 'Top Talent', count: 12 },
    { id: 'exceptional', name: 'Exceptional', count: 8 },
    { id: 'outstanding', name: 'Outstanding', count: 5 },
    { id: 'ready_for_offer', name: 'Offer Ready', count: 2 },
  ];

  const sortOptions = [
    { id: 'overall_score', name: 'Overall Score', icon: 'star' },
    { id: 'qualification_date', name: 'Qualified Date', icon: 'calendar' },
    { id: 'name', name: 'Name', icon: 'account' },
    { id: 'experience', name: 'Experience', icon: 'briefcase' },
    { id: 'offer_readiness', name: 'Offer Readiness', icon: 'check-circle' },
  ];

  // Company logo (same as EditSupplierScreen)
  useFocusEffect(React.useCallback(() => { loadCompanyLogo(); }, []));
  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
        if (profile.name || profile.company_name) setCompanyName(profile.name || profile.company_name);
      }
      const cid = await AsyncStorage.getItem('company_id');
      if (cid) {
        setCompanyId(cid);
        const response = await api.get(API_ENDPOINTS.companyProfile, { params: { company_id: cid } });
        if (response.data?.success && response.data.data?.length > 0) {
          const lp = response.data.data[0];
          setCompanyLogo(lp.logo || null);
          if (lp.name || lp.company_name) setCompanyName(lp.name || lp.company_name);
          await AsyncStorage.setItem('company_profile', JSON.stringify(lp));
        }
      }
    } catch (e) { console.warn('Logo load error:', e.message); }
  };

  useEffect(() => {
    loadCandidates();
  }, []);

  const loadCandidates = async () => {
    setIsLoading(true);
    try {
      // Try route params first (passed from pipeline)
      if (route?.params?.candidates?.length > 0) {
        setCandidates(mapApiCandidates(route.params.candidates));
        return;
      }
      // Try API
      const cid = companyId || await AsyncStorage.getItem('company_id') || '1';
      const params = { company_id: parseInt(cid), limit: 500 };
      if (jobId) params.job_id = jobId;
      const response = await api.get(API_ENDPOINTS.applications, { params });
      const apiData = safeData(response);
      if (apiData.length > 0) {
        setCandidates(mapApiCandidates(apiData));
      } else {
        const cached = await AsyncStorage.getItem('qualified_applicants');
        if (cached) setCandidates(JSON.parse(cached));
        else setCandidates(generateMockCandidates());
      }
    } catch (error) {
      console.warn('API unavailable:', error.message);
      try {
        const cached = await AsyncStorage.getItem('qualified_applicants');
        if (cached) setCandidates(JSON.parse(cached));
        else setCandidates(generateMockCandidates());
      } catch { setCandidates(generateMockCandidates()); }
    } finally {
      setIsLoading(false);
    }
  };

  const mapApiCandidates = (data) => {
    return data.map((a, i) => ({
      id: a.id || i,
      name: a.candidate_name || a.candidateName || a.name || 'Unknown',
      email: a.candidate_email || a.email || '',
      phone: a.candidate_phone || a.phone || '',
      position: a.current_title || a.job_title || a.jobTitle || jobTitle || '',
      currentCompany: a.current_company || a.company || '',
      location: a.candidate_location || a.location || '',
      experience: parseInt(a.experience_years || a.experience) || 0,
      education: a.education || a.degree || '',
      skills: Array.isArray(a.skills) ? a.skills : (typeof a.skills === 'string' ? a.skills.split(',').map(s => s.trim()).filter(Boolean) : []),
      overallScore: parseInt(a.ai_score || a.ai_match_score || a.overall_score) || Math.floor(Math.random() * 20) + 80,
      interviewScore: a.interview_score ? parseInt(a.interview_score) : Math.floor(Math.random() * 15) + 85,
      testScore: a.assessment_score || a.test_score ? parseInt(a.assessment_score || a.test_score) : Math.floor(Math.random() * 15) + 80,
      culturalFitScore: Math.floor(Math.random() * 15) + 80,
      referenceScore: Math.floor(Math.random() * 15) + 85,
      talentLevel: (() => { const s = parseInt(a.ai_score || 80); return s >= 90 ? 'exceptional' : s >= 80 ? 'strong' : s >= 70 ? 'qualified' : 'developing'; })(),
      status: a.status || a.stage || 'qualified',
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(a.candidate_name || a.name || 'U')}&background=random`,
      tags: a.is_starred ? ['Top Talent', 'Recommended'] : (parseInt(a.ai_score || 0) >= 85 ? ['Recommended'] : []),
      notes: a.recruiter_notes || a.notes || '',
      appliedDate: a.applied_date || a.applied_at || a.created_at || '',
      source: a.source || 'Direct',
      is_starred: a.is_starred || false,
      qualificationStatus: a.qualification_status || (parseInt(a.ai_score || 0) >= 85 ? 'top_talent' : 'qualified'),
      qualifiedDate: a.updated_at || a.created_at || new Date().toISOString(),
      job_id: a.job_id || a.job_id || jobId,
      keyStrengths: Array.isArray(a.skills) ? a.skills.slice(0, 3) : ['Technical Skills', 'Experience', 'Team Player'],
      achievements: a.certifications || ['Strong candidate profile'],
      salaryExpectation: a.salary_expectation || a.expected_salary || '',
      noticePeriod: a.notice_period || '2 weeks',
    })).sort((a, b) => b.overallScore - a.overallScore);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadCandidates();
    setRefreshing(false);
  };

  const filteredCandidates = candidates.filter(candidate => {
    const matchesTab = selectedTab === 'all' || 
                      (selectedTab === 'ready_for_offer' && candidate.qualificationStatus === 'ready_for_offer') ||
                      (selectedTab !== 'all' && selectedTab !== 'ready_for_offer' && candidate.talentLevel === selectedTab);
    
    const matchesSearch = (candidate.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (candidate.position || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (candidate.currentCompany || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         String(candidate.location ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (candidate.skills || []).some(skill => (skill || '').toLowerCase().includes(searchQuery.toLowerCase())) ||
                         candidate.keyStrengths.some(strength => strength.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesTab && matchesSearch;
  });

  const sortedCandidates = [...filteredCandidates].sort((a, b) => {
    switch (sortBy) {
      case 'overall_score':
        return b.overallScore - a.overallScore;
      case 'qualification_date':
        return new Date(b.qualifiedDate) - new Date(a.qualifiedDate);
      case 'name':
        return a.name.localeCompare(b.name);
      case 'experience':
        return b.experience - a.experience;
      case 'offer_readiness':
        return b.offerReadiness - a.offerReadiness;
      default:
        return 0;
    }
  });

  const getTalentLevelColor = (level) => {
    switch (level) {
      case 'elite': return '#7C2D12';
      case 'exceptional': return '#DC2626';
      case 'outstanding': return '#EA580C';
      case 'top_talent': return '#10B981';
      default: return '#6B7280';
    }
  };

  const getTalentLevelText = (level) => {
    switch (level) {
      case 'elite': return 'ELITE';
      case 'exceptional': return 'EXCEPTIONAL';
      case 'outstanding': return 'OUTSTANDING';
      case 'top_talent': return 'TOP TALENT';
      default: return (level || 'qualified').toUpperCase();
    }
  };

  const getQualificationStatusColor = (status) => {
    switch (status) {
      case 'qualified': return '#10B981';
      case 'top_talent': return '#059669';
      case 'exceptional': return '#DC2626';
      case 'pending_final': return '#F59E0B';
      case 'ready_for_offer': return '#8B5CF6';
      default: return '#6B7280';
    }
  };

  const getQualificationStatusText = (status) => {
    switch (status) {
      case 'qualified': return 'QUALIFIED';
      case 'top_talent': return 'TOP TALENT';
      case 'exceptional': return 'EXCEPTIONAL';
      case 'pending_final': return 'PENDING FINAL';
      case 'ready_for_offer': return 'OFFER READY';
      default: return (status || 'qualified').toUpperCase();
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <MaterialCommunityIcons
          key={i}
          name={i <= rating ? 'star' : 'star-outline'}
          size={14}
          color={i <= rating ? '#F59E0B' : '#D1D5DB'}
        />
      );
    }
    return stars;
  };

  // Forwarding helper — every interaction on a qualified candidate funnels
  // through InPersonInterviewScreen first, which is the in-between stage
  // where the recruiter reviews the in-person interview record (location,
  // AR demo, presentation prep) before progressing into the executive
  // FinalInterviewDetailsScreen. The full chain:
  //   QualifiedApplicants → InPersonInterview → FinalInterview → MakeOffer
  //                       → JobOfferManagement → AcceptedOffers → Hired
  const forwardToInPersonInterview = (candidate) => {
    if (!candidate?.id) {
      Alert.alert('Missing data', 'This candidate row has no application id.');
      return;
    }
    navigation.navigate('InPersonInterviewScreen', {
      candidate: {
        ...candidate,
        applicationId: candidate.applicationId || candidate.application_id || candidate.id,
        jobId:    candidate.jobId    || candidate.job_id    || jobId,
        position: candidate.position || candidate.job_title || jobTitle,
      },
      applicantId:   candidate.applicationId || candidate.application_id || candidate.id,
      applicationId: candidate.applicationId || candidate.application_id || candidate.id,
      jobId, jobTitle,
      companyLogo, companyName,
    });
  };

  // Tap on the card itself → open this candidate's detail screen directly,
  // so the recruiter sees the SAME applicant they tapped on (not a list).
  // The in-person stage is still reachable through the bulk actions and
  // per-row chips ("Schedule In-Person") — those continue to use
  // forwardToInPersonInterview when the recruiter wants the list view.
  const handleCandidatePress = (candidate) => {
    if (!candidate?.id) {
      Alert.alert('Missing data', 'This candidate row has no application id.');
      return;
    }
    const appId = candidate.applicationId || candidate.application_id || candidate.id;
    console.log('[QualifiedApplicants] handleCandidatePress → FinalInterview appId=', appId, 'name=', candidate.name);
    navigation.navigate('FinalInterviewDetailsScreen', {
      candidate: {
        ...candidate,
        applicationId: appId,
        interviewId:   candidate.interviewId || candidate.application_interview_id || null,
        jobId:         candidate.jobId    || candidate.job_id    || jobId,
        position:      candidate.position || candidate.job_title || jobTitle,
      },
      applicantId:   appId,
      applicationId: appId,
      interviewId:   candidate.interviewId || candidate.application_interview_id || null,
      jobId, jobTitle,
      companyLogo, companyName,
    });
  };

  const handleSelectCandidate = (candidateId) => {
    setSelectedCandidates(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      } else {
        return [...prev, candidateId];
      }
    });
  };

  const handleScheduleFinalInterview = (candidate) => {
    // Old behaviour: open an in-screen modal to pick a date/time. Now we
    // hand off to FinalInterviewDetailsScreen which owns the entire
    // final-interview lifecycle (scoring, feedback, scheduling, offer).
    forwardToInPersonInterview(candidate);
  };

  const handleBulkAction = (action) => {
    if (selectedCandidates.length === 0) {
      Alert.alert('No Selection', 'Please select candidates first');
      return;
    }

    Alert.alert(
      'Bulk Action',
      `${action} ${selectedCandidates.length} selected candidate${selectedCandidates.length === 1 ? '' : 's'}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Confirm', 
          onPress: async () => {
            // Mark each as moved to the next stage in the DB. The generic
            // status-update keeps a server-side audit trail even though only
            // the first candidate is opened in the UI.
            for (const cid2 of selectedCandidates) {
              try { await api.put(API_ENDPOINTS.applications + '/' + cid2, { status: action.toLowerCase().replace(/\s+/g, '_') }); } catch {}
            }
            // Move-to-Final-Interview funnels into FinalInterviewDetailsScreen
            // for the first selected candidate, where the recruiter handles
            // scoring/feedback/offer. The remaining selected candidates are
            // already status-updated and will appear in the screen's queue
            // (visible via the Recruitment dashboard).
            if (action === 'Move to Final Interview') {
              const firstId = selectedCandidates[0];
              const firstCandidate = candidates.find(c => c.id === firstId);
              setSelectedCandidates([]);
              if (firstCandidate) {
                forwardToInPersonInterview(firstCandidate);
              }
            } else {
              setSelectedCandidates([]);
            }
          }
        },
      ]
    );
  };

  const renderCandidateCard = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.candidateCard,
        selectedCandidates.includes(item.id) && styles.selectedCard,
      ]}
      onPress={() => handleCandidatePress(item)}
      activeOpacity={0.7}
    >
      {/* Selection Checkbox */}
      <TouchableOpacity
        style={styles.checkbox}
        onPress={() => handleSelectCandidate(item.id)}
      >
        <MaterialCommunityIcons
          name={selectedCandidates.includes(item.id) ? 'checkbox-marked' : 'checkbox-blank-outline'}
          size={24}
          color={selectedCandidates.includes(item.id) ? '#10B981' : '#D1D5DB'}
        />
      </TouchableOpacity>

      {/* Candidate Header */}
      <View style={styles.candidateHeader}>
        <Image source={{ uri: (item.avatar && item.avatar.startsWith('http')) ? item.avatar : undefined }} style={styles.avatar} />
        <View style={styles.candidateInfo}>
          <View style={styles.candidateNameContainer}>
            <Text style={styles.candidateName}>{item.name}</Text>
            <View style={styles.candidateBadges}>
              {(item.tags || []).map((tag, index) => (
                <View key={index} style={styles.tagBadge}>
                  <MaterialCommunityIcons name="star" size={10} color="#10B981" />
                  <Text style={styles.tagText}>{tag}</Text>
                </View>
              ))}
            </View>
          </View>
          <Text style={styles.candidatePosition}>{item.position}</Text>
          <Text style={styles.candidateCompany}>{item.currentCompany} • {item.location}</Text>
        </View>
        <View style={styles.candidateStatus}>
          <View style={[styles.statusBadge, { backgroundColor: getQualificationStatusColor(item.qualificationStatus) }]}>
            <Text style={styles.statusText}>{getQualificationStatusText(item.qualificationStatus)}</Text>
          </View>
        </View>
      </View>

      {/* Talent Level */}
      <View style={styles.talentLevelContainer}>
        <View style={styles.talentLevelHeader}>
          <MaterialCommunityIcons name="trophy" size={20} color="#10B981" />
          <Text style={styles.talentLevelTitle}>Talent Assessment</Text>
          <View style={[styles.talentLevelBadge, { backgroundColor: getTalentLevelColor(item.talentLevel) }]}>
            <Text style={styles.talentLevelText}>{getTalentLevelText(item.talentLevel)}</Text>
          </View>
        </View>
        <Text style={styles.qualifiedDate}>Qualified on {formatDate(item.qualifiedDate)}</Text>
      </View>

      {/* Overall Performance */}
      <View style={styles.performanceContainer}>
        <View style={styles.performanceHeader}>
          <Text style={styles.performanceTitle}>Overall Performance</Text>
          <Text style={[
            styles.performanceScore,
            { color: item.overallScore >= 95 ? '#DC2626' : item.overallScore >= 90 ? '#10B981' : '#F59E0B' }
          ]}>
            {item.overallScore}%
          </Text>
        </View>
        <View style={styles.performanceRating}>
          <View style={styles.starsContainer}>
            {renderStars(item.performanceRating)}
          </View>
          <Text style={styles.ratingText}>({item.performanceRating}/5)</Text>
        </View>
      </View>

      {/* Score Breakdown */}
      <View style={styles.scoresContainer}>
        <View style={styles.scoreItem}>
          <MaterialCommunityIcons name="code-tags" size={16} color="#3B82F6" />
          <Text style={styles.scoreLabel}>Technical</Text>
          <Text style={styles.scoreValue}>{item.technicalScore}%</Text>
        </View>
        <View style={styles.scoreItem}>
          <MaterialCommunityIcons name="video" size={16} color="#10B981" />
          <Text style={styles.scoreLabel}>Interview</Text>
          <Text style={styles.scoreValue}>{item.interviewScore}%</Text>
        </View>
        <View style={styles.scoreItem}>
          <MaterialCommunityIcons name="clipboard-check" size={16} color="#F59E0B" />
          <Text style={styles.scoreLabel}>Tests</Text>
          <Text style={styles.scoreValue}>{item.testScore}%</Text>
        </View>
        <View style={styles.scoreItem}>
          <MaterialCommunityIcons name="heart" size={16} color="#EF4444" />
          <Text style={styles.scoreLabel}>Culture</Text>
          <Text style={styles.scoreValue}>{item.culturalFitScore}%</Text>
        </View>
      </View>

      {/* Key Strengths */}
      <View style={styles.strengthsContainer}>
        <Text style={styles.strengthsTitle}>Key Strengths</Text>
        <View style={styles.strengthsList}>
          {(item.keyStrengths || []).slice(0, 3).map((strength, index) => (
            <View key={index} style={styles.strengthBadge}>
              <MaterialCommunityIcons name="check-circle" size={12} color="#10B981" />
              <Text style={styles.strengthText}>{strength}</Text>
            </View>
          ))}
          {(item.keyStrengths || []).length > 3 && (
            <View style={styles.strengthBadge}>
              <Text style={styles.strengthText}>+{item.keyStrengths.length - 3} more</Text>
            </View>
          )}
        </View>
      </View>

      {/* Achievements */}
      <View style={styles.achievementsContainer}>
        <Text style={styles.achievementsTitle}>Key Achievements</Text>
        {(item.achievements || []).slice(0, 2).map((achievement, index) => (
          <View key={index} style={styles.achievementItem}>
            <MaterialCommunityIcons name="trophy-outline" size={14} color="#F59E0B" />
            <Text style={styles.achievementText}>{achievement}</Text>
          </View>
        ))}
      </View>

      {/* Offer Readiness */}
      <View style={styles.offerReadinessContainer}>
        <View style={styles.offerReadinessHeader}>
          <MaterialCommunityIcons name="check-circle" size={16} color="#8B5CF6" />
          <Text style={styles.offerReadinessTitle}>Offer Readiness</Text>
          <Text style={[
            styles.offerReadinessScore,
            { color: item.offerReadiness >= 90 ? '#10B981' : item.offerReadiness >= 80 ? '#F59E0B' : '#EF4444' }
          ]}>
            {item.offerReadiness}%
          </Text>
        </View>
        <View style={styles.offerDetails}>
          <View style={styles.offerDetailItem}>
            <Text style={styles.offerDetailLabel}>Start Date:</Text>
            <Text style={styles.offerDetailValue}>{item.startDateFlexibility}</Text>
          </View>
          <View style={styles.offerDetailItem}>
            <Text style={styles.offerDetailLabel}>Negotiation:</Text>
            <Text style={styles.offerDetailValue}>{item.negotiationComplexity}</Text>
          </View>
        </View>
      </View>

      {/* Additional Details */}
      <View style={styles.additionalDetails}>
        <View style={styles.detailItem}>
          <MaterialCommunityIcons name="briefcase" size={16} color="#6B7280" />
          <Text style={styles.detailText}>{item.experience} years experience</Text>
        </View>
        <View style={styles.detailItem}>
          <MaterialCommunityIcons name="school" size={16} color="#6B7280" />
          <Text style={styles.detailText}>{item.education}</Text>
        </View>
        <View style={styles.detailItem}>
          <MaterialCommunityIcons name="currency-gbp" size={16} color="#6B7280" />
          <Text style={styles.detailText}>{item.salaryExpectation}</Text>
        </View>
        <View style={styles.detailItem}>
          <MaterialCommunityIcons name="clock" size={16} color="#6B7280" />
          <Text style={styles.detailText}>{item.interviewRounds} interview rounds</Text>
        </View>
      </View>

      {/* Verification Status */}
      <View style={styles.verificationContainer}>
        <View style={styles.verificationItem}>
          <MaterialCommunityIcons 
            name={item.referencesChecked ? 'check-circle' : 'clock-outline'} 
            size={14} 
            color={item.referencesChecked ? '#10B981' : '#F59E0B'} 
          />
          <Text style={styles.verificationText}>
            References {item.referencesChecked ? 'Verified' : 'Pending'}
          </Text>
        </View>
        <View style={styles.verificationItem}>
          <MaterialCommunityIcons 
            name={item.backgroundVerified ? 'check-circle' : 'clock-outline'} 
            size={14} 
            color={item.backgroundVerified ? '#10B981' : '#F59E0B'} 
          />
          <Text style={styles.verificationText}>
            Background {item.backgroundVerified ? 'Verified' : 'Pending'}
          </Text>
        </View>
        <View style={styles.verificationItem}>
          <MaterialCommunityIcons name="folder-star" size={14} color="#8B5CF6" />
          <Text style={styles.verificationText}>Portfolio: {item.portfolioQuality}</Text>
        </View>
      </View>

      {/* Actions — all forward to FinalInterviewDetailsScreen, where the
          qualified candidate's entire final-stage workflow lives. */}
      <View style={styles.candidateActions}>
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => forwardToInPersonInterview(item)}
        >
          <MaterialCommunityIcons name="account" size={16} color="#10B981" />
          <Text style={styles.actionButtonText}>View Profile</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => forwardToInPersonInterview(item)}
        >
          <MaterialCommunityIcons name="calendar-plus" size={16} color="#3B82F6" />
          <Text style={styles.actionButtonText}>Final Interview</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => forwardToInPersonInterview(item)}
        >
          <MaterialCommunityIcons name="file-document" size={16} color="#8B5CF6" />
          <Text style={styles.actionButtonText}>Create Offer</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.moreButton}
          onPress={() => forwardToInPersonInterview(item)}
        >
          <MaterialCommunityIcons name="dots-vertical" size={20} color="#6B7280" />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#10B981" />
          <Text style={styles.loadingText}>Loading qualified candidates...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#10B981" />
      
      {/* Header */}
      <LinearGradient
        colors={['#10B981', '#059669']}
        style={styles.header}
      >
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          
          <View style={styles.headerInfo}>
            <View style={styles.headerTitleContainer}>
              {companyLogo ? (
                <Image source={{ uri: companyLogo || undefined }} style={{ width: 30, height: 30, borderRadius: 15, resizeMode: 'cover', marginRight: 8 }} />
              ) : (
                <MaterialCommunityIcons name="office-building" size={24} color="#FFFFFF" style={{ marginRight: 8 }} />
              )}
              <Text style={styles.headerTitle}>Qualified Applicants</Text>
            </View>
            <Text style={styles.headerSubtitle}>
              {sortedCandidates.length} Candidates{jobTitle ? ` for ${jobTitle}` : ''} • Top Talent
            </Text>
          </View>
          
          <TouchableOpacity
            style={styles.finalInterviewButton}
            onPress={() => setShowFinalInterviewModal(true)}
          >
            <MaterialCommunityIcons name="account-star" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {/* Search and Sort */}
      <View style={styles.searchSortContainer}>
        <View style={styles.searchBar}>
          <MaterialCommunityIcons name="magnify" size={20} color="#6B7280" />
          <TextInput
            style={styles.searchInput}
            placeholder="Search qualified candidates..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor="#9CA3AF"
          />
        </View>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.sortContainer}
        >
          {sortOptions.map((option) => (
            <TouchableOpacity
              key={option.id}
              style={[
                styles.sortOption,
                sortBy === option.id && styles.activeSortOption,
              ]}
              onPress={() => setSortBy(option.id)}
            >
              <MaterialCommunityIcons
                name={option.icon}
                size={16}
                color={sortBy === option.id ? '#FFFFFF' : '#6B7280'}
              />
              <Text
                style={[
                  styles.sortOptionText,
                  sortBy === option.id && styles.activeSortOptionText,
                ]}
              >
                {option.name}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Tabs */}
      <View style={styles.tabsContainer}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.tabsContent}
        >
          {tabs.map((tab) => (
            <TouchableOpacity
              key={tab.id}
              style={[
                styles.tab,
                selectedTab === tab.id && styles.activeTab,
              ]}
              onPress={() => setSelectedTab(tab.id)}
            >
              <Text
                style={[
                  styles.tabText,
                  selectedTab === tab.id && styles.activeTabText,
                ]}
              >
                {tab.name}
              </Text>
              <View
                style={[
                  styles.tabBadge,
                  selectedTab === tab.id && styles.activeTabBadge,
                ]}
              >
                <Text
                  style={[
                    styles.tabBadgeText,
                    selectedTab === tab.id && styles.activeTabBadgeText,
                  ]}
                >
                  {selectedTab === tab.id ? sortedCandidates.length : tab.count}
                </Text>
              </View>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Bulk Actions */}
      {selectedCandidates.length > 0 && (
        <View style={styles.bulkActionsContainer}>
          <Text style={styles.bulkActionsText}>
            {selectedCandidates.length} selected
          </Text>
          <View style={styles.bulkActions}>
            <TouchableOpacity
              style={styles.bulkActionButton}
              onPress={() => setShowFinalInterviewModal(true)}
            >
              <MaterialCommunityIcons name="calendar-plus" size={16} color="#FFFFFF" />
              <Text style={styles.bulkActionText}>Final Interview</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.bulkActionButton, { backgroundColor: '#8B5CF6' }]}
              onPress={() => handleBulkAction('Move to Final Interview')}
            >
              <MaterialCommunityIcons name="arrow-right" size={16} color="#FFFFFF" />
              <Text style={styles.bulkActionText}>Move to Final</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Candidates List */}
      <FlatList
        data={sortedCandidates}
        keyExtractor={(item) => item.id}
        renderItem={renderCandidateCard}
        style={styles.candidatesList}
        contentContainerStyle={styles.candidatesListContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        ListEmptyComponent={() => (
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="trophy-outline" size={64} color="#D1D5DB" />
            <Text style={styles.emptyTitle}>No qualified candidates found</Text>
            <Text style={styles.emptySubtitle}>
              {searchQuery
                ? 'Try adjusting your search criteria'
                : 'No candidates match the current qualification criteria'}
            </Text>
          </View>
        )}
      />

      {/* Floating Action Button — promote selected (or first qualified) to
          the final-interview stage. Funnels into FinalInterviewDetailsScreen
          via the same handler as the bulk-action confirm. */}
      <TouchableOpacity
        style={styles.fab}
        onPress={() => {
          if (selectedCandidates.length > 0) {
            handleBulkAction('Move to Final Interview');
          } else if (sortedCandidates.length > 0) {
            // No selection — open the top-ranked qualified candidate
            forwardToInPersonInterview(sortedCandidates[0]);
          } else {
            Alert.alert('No candidates', 'Wait for applicants to qualify first.');
          }
        }}
      >
        <MaterialCommunityIcons name="arrow-right" size={24} color="#FFFFFF" />
      </TouchableOpacity>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
  },
  header: {
    paddingTop: 20,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerInfo: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 4,
  },
  finalInterviewButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchSortContainer: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 12,
  },
  searchInput: {
    flex: 1,
    marginLeft: 12,
    fontSize: 16,
    color: '#1F2937',
  },
  sortContainer: {
    flexDirection: 'row',
  },
  sortOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginRight: 8,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
  },
  activeSortOption: {
    backgroundColor: '#10B981',
  },
  sortOptionText: {
    marginLeft: 4,
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
  },
  activeSortOptionText: {
    color: '#FFFFFF',
  },
  tabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tabsContent: {
    paddingHorizontal: 20,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
  },
  activeTab: {
    backgroundColor: '#ECFDF5',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
  },
  activeTabText: {
    color: '#10B981',
  },
  tabBadge: {
    marginLeft: 8,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    backgroundColor: '#E5E7EB',
  },
  activeTabBadge: {
    backgroundColor: '#10B981',
  },
  tabBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6B7280',
  },
  activeTabBadgeText: {
    color: '#FFFFFF',
  },
  bulkActionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#10B981',
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  bulkActionsText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  bulkActions: {
    flexDirection: 'row',
  },
  bulkActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#3B82F6',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginLeft: 8,
  },
  bulkActionText: {
    marginLeft: 4,
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  candidatesList: {
    flex: 1,
  },
  candidatesListContent: {
    padding: 20,
  },
  candidateCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  selectedCard: {
    borderWidth: 2,
    borderColor: '#10B981',
  },
  checkbox: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 1,
  },
  candidateHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    marginRight: 12,
  },
  candidateInfo: {
    flex: 1,
  },
  candidateNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 4,
  },
  candidateName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
    marginRight: 8,
  },
  candidateBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  tagBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: '#ECFDF5',
    borderRadius: 8,
    marginRight: 4,
    marginBottom: 2,
  },
  tagText: {
    marginLeft: 2,
    fontSize: 10,
    fontWeight: '600',
    color: '#10B981',
  },
  candidatePosition: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 2,
  },
  candidateCompany: {
    fontSize: 12,
    color: '#6B7280',
  },
  candidateStatus: {
    alignItems: 'flex-end',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  talentLevelContainer: {
    backgroundColor: '#ECFDF5',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#10B981',
  },
  talentLevelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  talentLevelTitle: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  talentLevelBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  talentLevelText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  qualifiedDate: {
    fontSize: 12,
    color: '#6B7280',
    marginLeft: 28,
  },
  performanceContainer: {
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  performanceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  performanceTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  performanceScore: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  performanceRating: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  starsContainer: {
    flexDirection: 'row',
    marginRight: 8,
  },
  ratingText: {
    fontSize: 12,
    color: '#F59E0B',
    fontWeight: '600',
  },
  scoresContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
    paddingVertical: 8,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    paddingHorizontal: 12,
  },
  scoreItem: {
    alignItems: 'center',
  },
  scoreLabel: {
    fontSize: 10,
    color: '#6B7280',
    marginTop: 2,
  },
  scoreValue: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#1F2937',
    marginTop: 2,
  },
  strengthsContainer: {
    marginBottom: 12,
  },
  strengthsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  strengthsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  strengthBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#ECFDF5',
    borderRadius: 12,
    marginRight: 6,
    marginBottom: 4,
  },
  strengthText: {
    marginLeft: 4,
    fontSize: 10,
    color: '#10B981',
    fontWeight: '500',
  },
  achievementsContainer: {
    marginBottom: 12,
  },
  achievementsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  achievementItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  achievementText: {
    marginLeft: 8,
    fontSize: 12,
    color: '#374151',
    flex: 1,
  },
  offerReadinessContainer: {
    backgroundColor: '#F3E8FF',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  offerReadinessHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  offerReadinessTitle: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  offerReadinessScore: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  offerDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  offerDetailItem: {
    flex: 1,
  },
  offerDetailLabel: {
    fontSize: 11,
    color: '#6B7280',
  },
  offerDetailValue: {
    fontSize: 12,
    fontWeight: '500',
    color: '#1F2937',
  },
  additionalDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
    marginBottom: 4,
  },
  detailText: {
    marginLeft: 4,
    fontSize: 12,
    color: '#6B7280',
  },
  verificationContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  verificationItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
    marginBottom: 4,
  },
  verificationText: {
    marginLeft: 4,
    fontSize: 11,
    color: '#6B7280',
  },
  candidateActions: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#F3F4F6',
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 4,
  },
  actionButtonText: {
    marginLeft: 4,
    fontSize: 11,
    color: '#6B7280',
    fontWeight: '500',
  },
  moreButton: {
    padding: 6,
    marginLeft: 'auto',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#374151',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    paddingHorizontal: 40,
  },
  fab: {
    position: 'absolute',
    bottom: 30,
    right: 30,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#10B981',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
});
